import { MessageCircle, ClipboardList, CalendarDays, Receipt, ChevronRight } from 'lucide-react';
import { contar, horaDoDia, rotuloDeDia } from '@fiodecobre/shared';
import type { ClienteApp, ResumoApp } from './api';

// A HOME do aplicativo do cliente ([DA-575]).
//
// Gatilho dele, 18/08/2026: *"Esta tela de entrada nao esta legal. Gostaria que o
// app abrisse com um menu, com as principais opcoes."* Ate aqui o `/app` abria
// direto no CHAT, e tudo o mais (Contas a Pagar, Orcamentos) vivia escondido atras
// do ☰ — ou seja, as duas areas que o app ganhou depois da Onda 1 so existiam pra
// quem abrisse o menu e procurasse. Recurso que nao aparece na tela nao existe
// (principio 1 do `sistema/CLAUDE.md`).
//
// ⚠️ **Cada linha DIZ o proprio estado.** Menu cujas linhas so repetem o nome nao
// ajuda ninguem a decidir onde tocar — e o numero embaixo de cada uma e' o que faz
// a home valer a viagem em vez de ser um pedagio antes do chat. Os quatro estados
// saem de uma chamada so (`GET /api/app/resumo`).
//
// ⚠️ **A ordem nao e' alfabetica nem por importancia da loja: e' por frequencia de
// uso do CLIENTE.** Chat primeiro porque e' o que ele abre todo dia e o que a
// notificacao aponta; Financeiro por ultimo porque e' consulta de vez em
// quando. Quem chega pela notificacao nem passa por aqui — o aviso continua caindo
// direto na conversa, e trocar isso por "cai na home" pioraria justamente o que o
// app existe pra fazer.

const COBRE = '#b45309';

/**
 * Lado do icone de cada linha.
 *
 * Medido no iPhone dele (375x762 CSS): com a lista ocupando a altura toda, cada
 * linha fica em ~147px. A 48 o icone ocupava 33% da linha e o conjunto boiava num
 * botao alto e vazio — que e' o oposto do que ele pediu ao mandar *"aumente o
 * tamanho vertical dos botoes e dos icones para ocupar os espacos em branco"*.
 * Crescer a linha sem crescer o conteudo so troca o vazio de lugar.
 */
const ICONE = 60;

interface Props {
  cliente: ClienteApp;
  /** `null` enquanto a 1a leitura nao voltou: a linha mostra o nome sem estado, e
   *  NUNCA um zero — zero e' uma afirmacao, e ainda nao ha o que afirmar. */
  resumo: ResumoApp | null;
  aoAbrirChat: () => void;
  aoAbrirOrcamentos: () => void;
  aoAbrirAgenda: () => void;
  aoAbrirFinanceiro: () => void;
}

/**
 * O que a linha da Agenda diz quando ha compromisso marcado.
 *
 * ⚠️ O dia sai por extenso relativo (`hoje`/`amanhã`/`qui, 21 ago`) pela mesma razao
 * da lista: ele le sem contar dias no dedo. E o "+N" so aparece quando ha mais de um,
 * porque "1 compromisso · hoje 14:30" repete a mesma informacao duas vezes.
 */
function proximoCompromisso(resumo: ResumoApp): string {
  // `agenda` e' `null` so no modo espelho ([DA-634]), e la esta funcao nem e'
  // chamada — o `?? 0` existe pro tipo, nao pra um caso real.
  const quantos = resumo.agenda ?? 0;
  if (!resumo.agenda_proximo) return contar(quantos, 'compromisso');
  const d = new Date(resumo.agenda_proximo);
  const quem = (resumo.agenda_proximo_cliente ?? '').trim();
  const base = `${rotuloDeDia(d, new Date())} ${horaDoDia(d)}${quem ? ` · ${quem}` : ''}`;
  return quantos > 1 ? `${base} · +${quantos - 1}` : base;
}

/** O que as duas áreas privadas dizem no espelho ([DA-634]). Frase única: elas
 *  contam a mesma coisa, e dois textos diferentes fariam procurar diferença. */
const PRIVADO_NO_ESPELHO = 'Área privada do prestador — não abre no modo espelho';

export function HomeApp({
  cliente, resumo, aoAbrirChat, aoAbrirOrcamentos, aoAbrirAgenda, aoAbrirFinanceiro,
}: Props): React.ReactElement {
  const temFicha = cliente.tem_ficha;
  const espelho = !!cliente.espelho;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px 20px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '2px 2px 14px' }}>
        {/* A LOGO do prestador ao lado do nome (pedido dele, 18/08 — migration 0237).
            ⚠️ Ela e' JPEG com fundo BRANCO cravado: o `MarcaPrestador` achata a
            transparencia na hora de subir, porque logo transparente vira preta em
            JPEG e o papel do orcamento e' branco. Entao a moldura aqui e' branca
            tambem — sobre o cinza da pagina, a foto apareceria como um retangulo
            branco torto. `contain` porque logo nao se corta: cortar tira justamente
            o nome da empresa, que e' o que ela tem de identificacao.
            Sem logo, nada aparece — o convite pra subir uma mora no ☰ → Config, e
            repetir aqui uma moldura tracejada vazia poria uma tarefa administrativa
            no meio da tela de trabalho dele. */}
        {cliente.logo_url && (
          <img
            src={cliente.logo_url}
            alt=""
            style={{
              width: 52, height: 52, flexShrink: 0, borderRadius: 12,
              objectFit: 'contain', backgroundColor: '#fff',
              border: '1px solid #e7e5e4', padding: 3,
            }}
          />
        )}
        <div style={{ fontSize: 14, color: '#57534e', lineHeight: 1.4, minWidth: 0 }}>
          Olá,
          {/* A MARCA vem antes do nome da pessoa: quem configurou "FIODECOBRE
              INSTALACOES" quer ver a propria empresa aqui, nao o nome do RG. Sem
              marca, o nome faz o papel — ninguem fica sem saudacao. */}
          <b style={{ display: 'block', fontSize: 19, fontWeight: 800, color: '#1c1917', letterSpacing: 0.2 }}>
            {cliente.fantasia || cliente.nome}
          </b>
        </div>
      </div>

      {/* ⚠️ A lista OCUPA a altura que sobra, e cada linha divide esse espaco por
          igual (`flex: 1` em cada uma) — pedido dele em 18/08, vendo a home no
          proprio iPhone: *"aumente o tamanho vertical dos botoes e dos icones para
          ocupar os espacos em branco"*. A 1a versao centralizava um bloco compacto e
          sobrava tela em cima e embaixo.

          O `minHeight` de cada linha e' a rede: num aparelho baixo (ou com o teclado
          aberto num futuro campo de busca) as linhas param de encolher e a lista
          rola, em vez de espremer o texto ate ele quebrar. */}
      <div style={{
        backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden',
        border: '1px solid #e7e5e4', flex: 1,
        display: 'flex', flexDirection: 'column',
      }}>
        <LinhaMenu
          icone={<MessageCircle size={ICONE} />}
          nome="Chat"
          estado="Fale com a loja"
          direita={resumo && resumo.nao_lidas > 0 ? <Pilula n={resumo.nao_lidas} /> : null}
          aoTocar={aoAbrirChat}
        />

        {/* ⛔ **No ESPELHO estas duas linhas viram cadeado** ([DA-634]) — Orçamentos
            e Agenda são conteúdo privado do prestador ([DA-565], decisão dele).
            Elas continuam APARECENDO, apagadas e dizendo o motivo: sumir do menu
            mudaria a tela que eu fui conferir, e a home tem que ser a mesma que o
            cliente vê ([DA-468]: ausência não se distingue de inexistência). */}
        <LinhaMenu
          icone={<ClipboardList size={ICONE} />}
          nome="Orçamentos"
          estado={
            espelho ? PRIVADO_NO_ESPELHO
              : resumo === null || resumo.orcamentos === null ? 'Monte o orçamento do seu cliente'
                : resumo.orcamentos === 0 ? 'Monte o orçamento do seu cliente e mande em PDF'
                  : `${contar(resumo.orcamentos, 'salvo')} · monte e mande em PDF`
          }
          apagado={espelho}
          aoTocar={espelho ? undefined : aoAbrirOrcamentos}
        />

        {/* AGENDA — no ar desde 19/08/2026 (migration 0239). Nasceu desligada, com
            selo EM BREVE, na onda da home ([DA-575]); o selo saiu quando a area
            passou a existir.
            ⚠️ **A linha mostra a HORA do proximo, nao so a contagem.** "hoje 14:30 ·
            DONA MARIA" responde a pergunta que faz alguem abrir a agenda; "2
            compromissos" so avisa que existe algo pra procurar — e obriga a abrir
            pra saber o que era. */}
        <LinhaMenu
          icone={<CalendarDays size={ICONE} />}
          nome="Agenda"
          estado={
            espelho ? PRIVADO_NO_ESPELHO
              : resumo === null || !resumo.agenda ? 'Seus compromissos de trabalho'
                : proximoCompromisso(resumo)
          }
          direita={!espelho && resumo && resumo.agenda ? <Selo>{String(resumo.agenda)}</Selo> : null}
          apagado={espelho}
          aoTocar={espelho ? undefined : aoAbrirAgenda}
        />

        {/* FINANCEIRO — a area ganhou uma 2a aba em 26/08/2026 (Notas Fiscais), e a
            linha foi renomeada junto: "Contas a Pagar" passou a nomear so metade do
            que mora ali dentro.
            ⚠️ **A 2a metade virou o ACERVO, e nao a contagem de notas das contas em
            aberto.** Medido no dia: **11 das 12 contas com ficha nao devem nada** —
            pra elas a linha antiga dizia apenas "Nada em aberto", que le como area
            vazia em quem tem 45 notas pra buscar. O numero e' o mesmo `total` que a
            aba mostra, pra linha e tela nao poderem divergir ([DA-590] p.6). */}
        <LinhaMenu
          icone={<Receipt size={ICONE} />}
          nome="Financeiro"
          estado={
            !temFicha
              ? 'Sua conta ainda não está ligada ao seu cadastro na loja. Fale com a gente pelo chat.'
              : resumo === null || resumo.titulos_abertos === null ? 'Suas contas, boletos e notas fiscais'
                : [
                  // ⚠️ **O boleto vem PRIMEIRO quando existe** ([DA-629]): das tres
                  // metades, e' a unica que pede acao com prazo. E o texto diz "para
                  // pagar" em vez de so contar — a palavra e' o que separa isto de
                  // mais um numero na linha.
                  resumo.boletos ? `${contar(resumo.boletos, 'boleto', 'boletos')} para pagar` : null,
                  resumo.titulos_abertos === 0 ? 'Nada em aberto' : `${contar(resumo.titulos_abertos, 'título em aberto', 'títulos em aberto')}`,
                  resumo.notas_total ? `${contar(resumo.notas_total, 'nota', 'notas')}` : null,
                ].filter(Boolean).join(' · ')
          }
          apagado={!temFicha}
          aoTocar={temFicha ? aoAbrirFinanceiro : undefined}
        />
      </div>
    </div>
  );
}

/**
 * Uma opcao do menu.
 *
 * `apagado` sem `aoTocar` desenha uma linha que NAO e' botao — e isso e' proposital:
 * botao que existe e nao faz nada ensina a duvidar dos outros. O motivo de estar
 * apagada vai sempre no proprio `estado`.
 */
function LinhaMenu({ icone, nome, estado, direita, apagado, aoTocar }: {
  icone: React.ReactNode;
  nome: string;
  estado: string;
  direita?: React.ReactNode;
  apagado?: boolean;
  aoTocar?: () => void;
}): React.ReactElement {
  const conteudo = (
    <>
      <span style={{ color: apagado ? '#a8a29e' : COBRE, flexShrink: 0, display: 'flex' }}>{icone}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 19, fontWeight: 700, color: apagado ? '#78716c' : '#1c1917' }}>
          {nome}
        </span>
        {/* 14px, escolhido por ele em 18/08 vendo a tela no proprio iPhone. O valor
            anterior (13,5) era um "+1" sobre o 12,5 original — numero quebrado que
            ninguem pediu, do tipo que nasce de aplicar delta sobre tamanho herdado
            em vez de escolher um absoluto. */}
        <span style={{ display: 'block', fontSize: 14, color: '#6b7280', marginTop: 4, lineHeight: 1.35 }}>
          {estado}
        </span>
      </span>
      {/* ⚠️ A bolinha e' um ACRESCIMO a seta, nunca um substituto. Na 1a versao ela
          ocupava o lugar do chevron, e a linha do Chat — a mais tocada de todas —
          ficava sendo a unica sem sinal de que abre alguma coisa. So apareceu
          medindo o build de verdade (2 setas pra 3 linhas clicaveis); no mockup eu
          tinha desenhado as duas coisas juntas sem reparar que o codigo nao fazia
          isso. Quem NAO abre nada (a Agenda) segue sem seta, e ai o selo fica
          sozinho — que e' o sinal correto. */}
      {direita}
      {aoTocar && <ChevronRight size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />}
    </>
  );

  const estilo: React.CSSProperties = {
    width: '100%', display: 'flex', alignItems: 'center', gap: 16,
    // `flex: 1` divide a altura da lista por igual entre as 4 linhas; o `minHeight`
    // impede que elas se espremam num aparelho baixo. Ver o comentario da lista.
    flex: 1, minHeight: 86,
    padding: '18px 16px', borderBottom: '1px solid #eeecea',
    textAlign: 'left', backgroundColor: apagado ? '#f7f6f5' : '#fff',
  };

  if (!aoTocar) return <div style={estilo}>{conteudo}</div>;
  return <button type="button" onClick={aoTocar} style={estilo}>{conteudo}</button>;
}

function Pilula({ n }: { n: number }): React.ReactElement {
  return (
    <span
      // O numero sozinho nao diz do que ele e'. Quem le com o dedo (VoiceOver,
      // TalkBack) recebe a frase inteira; quem le com o olho ja tem o contexto da
      // linha em volta.
      aria-label={contar(n, 'mensagem nova', 'mensagens novas')}
      style={{
        minWidth: 26, height: 26, padding: '0 8px', backgroundColor: COBRE, color: '#fff',
        borderRadius: 999, fontSize: 13.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

function Selo({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span style={{
      backgroundColor: '#e7e5e4', color: '#57534e', fontSize: 10, fontWeight: 800,
      letterSpacing: 0.6, padding: '3px 7px', borderRadius: 6, flexShrink: 0,
    }}>
      {children}
    </span>
  );
}
